import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import AnimatedSection from "@/components/AnimatedSection";
import CounterAnimation from "@/components/CounterAnimation";
import { useLang } from "@/i18n/LangContext";
import { Wrench, Route, Droplets, Truck, CircleDot, Sun, Trash2, Recycle, ShieldCheck, Clock, Award, Users } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1642781100314-f1ac6fb42296?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY29uc3RydWN0aW9uJTIwc2l0ZSUyMG5pZ2h0fGVufDB8fHx8MTc3NjIwNzQ0Mnww&ixlib=rb-4.1.0&q=85";

export default function TravauxPage() {
  const { t } = useLang();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const SERVICES = [
    { icon: Wrench, titleKey: "travService1Title", descKey: "travService1Desc" },
    { icon: Route, titleKey: "travService2Title", descKey: "travService2Desc" },
    { icon: Droplets, titleKey: "travService3Title", descKey: "travService3Desc" },
    { icon: Truck, titleKey: "travService4Title", descKey: "travService4Desc" },
    { icon: CircleDot, titleKey: "travService5Title", descKey: "travService5Desc" },
    { icon: Sun, titleKey: "travService6Title", descKey: "travService6Desc" },
    { icon: Trash2, titleKey: "travService7Title", descKey: "travService7Desc" },
    { icon: Recycle, titleKey: "travService8Title", descKey: "travService8Desc" },
  ];

  const STATS = [
    { value: 150, suffix: "+", labelKey: "travStatsProjects" },
    { value: 98, suffix: "%", labelKey: "travStatsSatisfaction" },
    { value: 50, suffix: "+", labelKey: "travStatsEquipment" },
    { value: 15, suffix: "+", labelKey: "travStatsExperience" },
  ];

  const PROCESS = [
    { num: "01", titleKey: "travProcess1Title", descKey: "travProcess1Desc" },
    { num: "02", titleKey: "travProcess2Title", descKey: "travProcess2Desc" },
    { num: "03", titleKey: "travProcess3Title", descKey: "travProcess3Desc" },
  ];

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="travaux-page">
      <Navbar />
      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden" data-testid="travaux-hero">
        <motion.div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-black/30" />
        <div className="relative z-10 px-6 sm:px-12 lg:px-24 pb-20 sm:pb-28 max-w-5xl">
          <motion.span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}>{t("travHeroOverline")}</motion.span>
          <motion.h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white font-light tracking-tight leading-[1.1]" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            {t("travHeroTitle1")}<br /><span className="text-[#C8A951]">{t("travHeroTitle2")}</span>
          </motion.h1>
          <motion.div className="h-[2px] bg-[#C8A951] w-16 mt-6" initial={{ width: 0 }} animate={{ width: 64 }} transition={{ delay: 0.9, duration: 0.6 }} />
        </div>
      </section>

      {/* About */}
      <section className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24" data-testid="travaux-about">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("travAboutOverline")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-6">{t("travAboutTitle")}</h2>
            <div className="h-[2px] bg-[#C8A951] w-12 mb-8" />
            <p className="text-[#A1A1AA] font-body text-base sm:text-lg leading-relaxed mb-6">{t("travAboutP1")}</p>
            <p className="text-[#A1A1AA] font-body text-base sm:text-lg leading-relaxed">{t("travAboutP2")}</p>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: ShieldCheck, labelKey: "professionnalisme", descKey: "professionnalismeDesc" },
                { icon: Award, labelKey: "maitriseTechnique", descKey: "maitriseTechniqueDesc" },
                { icon: Clock, labelKey: "respectDelaisShort", descKey: "respectDelaisShortDesc" },
                { icon: Users, labelKey: "equipeQualifiee", descKey: "equipeQualifieeDesc" },
              ].map((item, i) => (
                <div key={i} className="bg-[#1A1A1A] border border-white/5 p-6 group hover:border-[#C8A951]/30 transition-all duration-500">
                  <item.icon className="w-8 h-8 text-[#C8A951] mb-3" strokeWidth={1} />
                  <h4 className="text-white font-body text-sm font-medium mb-1">{t(item.labelKey)}</h4>
                  <p className="text-[#A1A1AA] text-xs font-body leading-relaxed">{t(item.descKey)}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0a0a0a]" data-testid="travaux-services">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("travServicesOverline")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-4">{t("travServicesTitle")}</h2>
            <div className="h-[2px] bg-[#C8A951] w-12 mb-16" />
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((service, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="bg-[#1A1A1A] border border-white/5 p-6 sm:p-8 group hover:border-[#C8A951]/30 transition-all duration-500 h-full" data-testid={`travaux-service-${i}`}>
                  <service.icon className="w-9 h-9 text-[#C8A951] mb-5 group-hover:scale-110 transition-transform duration-300" strokeWidth={1} />
                  <h3 className="font-heading text-lg text-white mb-3">{t(service.titleKey)}</h3>
                  <p className="text-[#A1A1AA] text-sm font-body leading-relaxed">{t(service.descKey)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24" data-testid="travaux-stats">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((stat, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="text-center" data-testid={`travaux-stat-${i}`}>
                  <CounterAnimation end={stat.value} suffix={stat.suffix} />
                  <p className="text-[#A1A1AA] text-sm tracking-[0.15em] uppercase mt-3 font-body">{t(stat.labelKey)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0a0a0a]" data-testid="travaux-process">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("travProcessOverline")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-4">{t("travProcessTitle")}</h2>
            <div className="h-[2px] bg-[#C8A951] w-12 mb-16" />
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PROCESS.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="relative border-l-2 border-[#C8A951]/20 pl-8 py-4" data-testid={`travaux-process-${i}`}>
                  <div className="absolute left-[-9px] top-6 w-4 h-4 bg-[#C8A951] rounded-full" />
                  <span className="text-[#C8A951] font-heading text-3xl block mb-2">{step.num}</span>
                  <h3 className="font-heading text-xl text-white mb-3">{t(step.titleKey)}</h3>
                  <p className="text-[#A1A1AA] text-sm font-body leading-relaxed">{t(step.descKey)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24">
        <AnimatedSection>
          <div className="max-w-4xl mx-auto text-center bg-[#1A1A1A] border border-white/5 p-12 sm:p-16">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white mb-4">{t("travCtaTitle")}</h2>
            <p className="text-[#A1A1AA] font-body mb-8 max-w-lg mx-auto">{t("travCtaDesc")}</p>
            <a href="#contact" className="inline-block bg-[#C8A951] text-black font-medium tracking-wide px-10 py-4 hover:bg-white transition-all duration-300 font-body uppercase text-sm" data-testid="travaux-cta-btn">{t("travCtaBtn")}</a>
          </div>
        </AnimatedSection>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-24 bg-[#0a0a0a]" data-testid="travaux-contact">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-[0.3em] text-[#C8A951] font-body block mb-4">{t("travContactOverline")}</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-normal tracking-tight mb-4">{t("travContactTitle")}</h2>
              <p className="text-[#A1A1AA] font-body max-w-lg mx-auto">{t("travContactDesc")}</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.2}><ContactForm source="travaux" /></AnimatedSection>
        </div>
      </section>
      <Footer />
    </div>
  );
}
